import { useEffect, useRef, useState } from 'react';
import type { EditorState } from '@xsscool/shared';
import { selectDirty, useStore } from '../store';
import {
  deleteShare,
  displayUrl,
  getShare,
  listMyShares,
  updateShare,
  type ShareSummary,
} from '../api';
import { SubmitPayloadModal } from './SubmitPayloadModal';

// One list of the caller's shares, used in two places:
//  - "manage" (topbar Shares ▾): rename, copy, open, submit, delete.
//  - "insert" (body toolbar): click a row to insert its replay URL.
interface Props {
  open: boolean;
  onClose: () => void;
  mode: 'manage' | 'insert';
  onPick?: (share: ShareSummary, replayUrl: string) => void;
}

export function relTime(ms: number): string {
  const diff = Date.now() - ms;
  const sec = Math.max(1, Math.floor(diff / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export function SharesMenu({ open, onClose, mode, onPick }: Props) {
  const domainBase = useStore((s) => s.domainBase);
  const domainSub = useStore((s) => s.domainSub);
  const currentShare = useStore((s) => s.currentShare);
  const dirty = useStore(selectDirty);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'updated' | 'created' | 'name'>('updated');

  const [shares, setShares] = useState<ShareSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState<{ share: ShareSummary; state: EditorState } | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      setShares(await listMyShares());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) void reload();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = () => void reload();
    window.addEventListener('xsscool:shares-changed', handler);
    return () => window.removeEventListener('xsscool:shares-changed', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    // The trigger button sits in the same wrapper as the menu. Clicks on it
    // must not count as "outside", or mousedown closes and click reopens.
    const onDoc = (e: MouseEvent) => {
      const wrap = ref.current?.parentElement ?? ref.current;
      if (wrap && !wrap.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const replayUrlFor = (s: ShareSummary) => displayUrl(`/s/${s.id}`, { domainBase, domainSub });
  const editorUrlFor = (s: ShareSummary) => displayUrl(`/e/${s.id}`, { domainBase, domainSub });

  const copy = (id: string, url: string) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      window.setTimeout(() => setCopiedId((cur) => (cur === id ? null : cur)), 1200);
    });
  };

  const removeShare = async (id: string) => {
    setBusyId(id);
    try {
      await deleteShare(id);
      setShares((prev) => prev.filter((s) => s.id !== id));
      setConfirmingDeleteId(null);
      if (currentShare?.id === id) {
        // The open share is gone: keep the content as scratch.
        useStore.getState().setCurrentShare(null);
        history.replaceState(null, '', '/');
      }
      window.dispatchEvent(new Event('xsscool:shares-changed'));
    } catch {
      /* swallow */
    } finally {
      setBusyId(null);
    }
  };

  const startRename = (s: ShareSummary) => {
    setRenamingId(s.id);
    setRenameValue(s.name ?? '');
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue('');
  };

  const submitRename = async (id: string) => {
    setBusyId(id);
    try {
      const saved = await updateShare(id, { name: renameValue.trim() || null });
      setShares((prev) => prev.map((s) => (s.id === id ? { ...s, name: saved.name } : s)));
      if (currentShare?.id === id) {
        useStore.getState().setCurrentShare({ ...currentShare, name: saved.name });
      }
      window.dispatchEvent(new Event('xsscool:shares-changed'));
      cancelRename();
    } catch {
      /* swallow */
    } finally {
      setBusyId(null);
    }
  };

  const openSubmit = async (s: ShareSummary) => {
    setBusyId(s.id);
    try {
      const detail = await getShare(s.id);
      if (detail?.state) setSubmitting({ share: s, state: detail.state });
    } finally {
      setBusyId(null);
    }
  };

  // Same tab unless the open share has unsaved edits; scratch is persisted
  // locally so it survives the navigation.
  const openInEditor = (s: ShareSummary) => {
    if (dirty) window.open(editorUrlFor(s), '_blank', 'noopener');
    else window.location.assign(editorUrlFor(s));
  };

  const visible = shares
    .filter((s) => {
      const q = query.trim().toLowerCase();
      return !q || (s.name ?? '').toLowerCase().includes(q) || s.preview.toLowerCase().includes(q) || s.id.toLowerCase().includes(q);
    })
    .sort((a, b) =>
      sort === 'name'
        ? (a.name ?? a.id).localeCompare(b.name ?? b.id)
        : sort === 'created'
          ? b.createdAt - a.createdAt
          : b.updatedAt - a.updatedAt,
    );

  return (
    <div className={'shares-menu' + (mode === 'manage' ? ' shares-drawer' : '')} ref={ref} role="dialog" aria-label="My shares">
      <div className="shares-head">
        <span>{mode === 'insert' ? 'Insert share URL' : 'My shares'}</span>
        <span className="shares-count">{shares.length}</span>
        {mode === 'manage' && (
          <button type="button" className="shares-close" onClick={onClose} aria-label="Close" title="Close (Esc)">×</button>
        )}
      </div>
      {shares.length > 3 && (
        <div className="shares-tools">
          <input
            type="search"
            placeholder="Search name, body or id"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search shares"
          />
          <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} aria-label="Sort shares">
            <option value="updated">Last edited</option>
            <option value="created">Newest</option>
            <option value="name">Name</option>
          </select>
        </div>
      )}

      {loading && shares.length === 0 && <p className="shares-empty">Loading…</p>}
      {!loading && shares.length === 0 && (
        <p className="shares-empty">Nothing saved yet. Press Save to create your first share.</p>
      )}

      {shares.length > 0 && visible.length === 0 && <p className="shares-empty">No shares match.</p>}
      {visible.length > 0 && (
        <ul className="shares-list">
          {visible.map((s) => (
            <li
              key={s.id}
              className={'share-item' + (currentShare?.id === s.id ? ' current' : '')}
              title={s.preview || undefined}
            >
              {renamingId === s.id ? (
                <form
                  className="share-rename"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void submitRename(s.id);
                  }}
                >
                  <input
                    type="text"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelRename();
                      }
                    }}
                    maxLength={80}
                    placeholder={s.id}
                    autoFocus
                    disabled={busyId === s.id}
                  />
                  <button type="submit" disabled={busyId === s.id} title="Save name">✓</button>
                  <button type="button" onClick={cancelRename} disabled={busyId === s.id} title="Cancel">×</button>
                </form>
              ) : mode === 'insert' ? (
                <button
                  type="button"
                  className="share-pick"
                  onClick={() => onPick?.(s, replayUrlFor(s))}
                  title="Insert this share's replay URL at the cursor"
                >
                  <span className="share-name">{s.name || s.id}</span>
                  <span className="share-preview">{s.preview}</span>
                  <span className="share-age">{relTime(s.updatedAt)}</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="share-name share-name-rename"
                    onClick={() => startRename(s)}
                    title="Click to rename"
                  >
                    {s.name || s.id}
                  </button>
                  {s.submittedPayloadId && (
                    <span className="share-submitted" title="Submitted to the public payload library">✓ submitted</span>
                  )}
                  <span className="share-age">{relTime(s.updatedAt)}</span>
                  {mode === 'manage' && s.preview && <span className="share-preview share-preview-row">{s.preview}</span>}
                  <span className="share-actions">
                    {confirmingDeleteId === s.id ? (
                      <>
                        <span className="share-confirm-label">Delete?</span>
                        <button type="button" className="share-confirm-yes" onClick={() => void removeShare(s.id)} disabled={busyId === s.id} title="Confirm delete" autoFocus>✓</button>
                        <button type="button" onClick={() => setConfirmingDeleteId(null)} disabled={busyId === s.id} title="Cancel">×</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => copy(s.id, replayUrlFor(s))} title="Copy replay URL to clipboard" aria-label="Copy replay URL">{copiedId === s.id ? '✓' : '⎘'}</button>
                        <button type="button" onClick={() => openInEditor(s)} disabled={busyId === s.id} title="Open in editor (/e/<id>)" aria-label="Open in editor">✎</button>
                        {!s.submittedPayloadId && (
                          <button type="button" onClick={() => void openSubmit(s)} disabled={busyId === s.id} title="Submit to the public payload library" aria-label="Submit to public library">↑</button>
                        )}
                        <button type="button" className="share-delete" onClick={() => setConfirmingDeleteId(s.id)} disabled={busyId === s.id} title="Delete">×</button>
                      </>
                    )}
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {mode === 'manage' && (
        <p className="shares-footnote">Anyone with a replay URL can load it. Shares keep until you delete them.</p>
      )}

      {submitting && (
        <SubmitPayloadModal
          state={submitting.state}
          initialTitle={submitting.share.name ?? ''}
          onClose={() => setSubmitting(null)}
          onSubmitted={async (created) => {
            try {
              await updateShare(submitting.share.id, { submittedPayloadId: created.id });
            } catch {
              /* local UI reconciles on next reload */
            }
            setSubmitting(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}
